import { saveOcrApiLog } from '@/api/ocr'
import ConstData from '@/constants/ConstData'
import debugLogger from '@/utils/debugLogger'

const REPORTED_DEDUPE_KEYS = new Set()
const REQUEST_ID_KEYS = [
  'requestId',
  'request_id',
  'req_id',
  'reqId',
  'transaction_id',
  'transactionId',
  'ekyc_transaction_id',
  'ekycTransactionId',
  'liveness_request_id',
  'dynaEkycTransactionId'
]
const TOKEN_KEYS = ['token', 'auth_token', 'authToken']

function normalizeKey(key = '') {
  return String(key).replace(/[_-]/g, '').toLowerCase()
}

function isSensitiveKey(key = '') {
  const normalizedKey = normalizeKey(key)
  return (
    normalizedKey === 'accountid' ||
    normalizedKey === 'appkey' ||
    normalizedKey === 'token' ||
    normalizedKey === 'authtoken'
  )
}

function sanitizeUrl(url = '') {
  if (typeof url !== 'string' || url.indexOf('?') === -1) {
    return url
  }

  const questionMarkIndex = url.lastIndexOf('?')
  if (questionMarkIndex === -1) {
    return url
  }

  const prefix = url.slice(0, questionMarkIndex)
  const queryString = url.slice(questionMarkIndex + 1)
  if (!queryString) {
    return url
  }

  const nextQueryString = queryString
    .split('&')
    .filter(Boolean)
    .filter(param => {
      const [rawKey] = param.split('=')
      if (!rawKey) {
        return false
      }

      return !isSensitiveKey(decodeURIComponent(rawKey))
    })
    .join('&')

  return nextQueryString ? `${prefix}?${nextQueryString}` : prefix
}

function sanitizeString(value = '') {
  const normalizedValue = String(value)

  if (
    normalizedValue.indexOf('account_id=') !== -1 ||
    normalizedValue.indexOf('app_key=') !== -1 ||
    normalizedValue.indexOf('accountId=') !== -1 ||
    normalizedValue.indexOf('appKey=') !== -1
  ) {
    return sanitizeUrl(normalizedValue)
  }

  if (normalizedValue.length > 3000) {
    return `[trimmed length=${normalizedValue.length} prefix=${normalizedValue.slice(0, 128)}]`
  }

  return normalizedValue
}

function sanitizePayload(payload, visited = new WeakMap()) {
  if (payload === null || payload === undefined) {
    return payload
  }

  if (typeof payload === 'string') {
    return sanitizeString(payload)
  }

  if (typeof payload !== 'object') {
    return payload
  }

  if (visited.has(payload)) {
    return visited.get(payload)
  }

  if (Array.isArray(payload)) {
    const nextArray = []
    visited.set(payload, nextArray)

    payload.forEach(item => {
      nextArray.push(sanitizePayload(item, visited))
    })

    return nextArray
  }

  const nextObject = {}
  visited.set(payload, nextObject)

  Object.keys(payload).forEach(key => {
    if (isSensitiveKey(key) || normalizeKey(key) === 'imagebase64') {
      return
    }

    nextObject[key] = sanitizePayload(payload[key], visited)
  })

  return nextObject
}

function stringifyPayload(payload) {
  try {
    return JSON.stringify(payload)
  } catch (error) {
    return JSON.stringify({
      message: 'dyna liveweb api log stringify failed',
      error: error && error.message ? error.message : String(error)
    })
  }
}

function findNestedValue(payload, keys = [], visited = new WeakSet()) {
  if (payload === undefined || payload === null || payload === '') {
    return ''
  }

  if (typeof payload !== 'object') {
    return ''
  }

  if (visited.has(payload)) {
    return ''
  }

  visited.add(payload)

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      const value = payload[key]
      if (value !== undefined && value !== null && value !== '') {
        return value
      }
    }
  }

  const values = Array.isArray(payload) ? payload : Object.values(payload)
  for (const value of values) {
    const matchedValue = findNestedValue(value, keys, visited)
    if (matchedValue !== undefined && matchedValue !== null && matchedValue !== '') {
      return matchedValue
    }
  }

  return ''
}

function buildDynaLiveWebApiLogPayload(action, requestParams = {}, response = null) {
  const sanitizedRequestParams = sanitizePayload(requestParams)
  const sanitizedResponse = sanitizePayload(response)
  const reqId = findNestedValue(response, REQUEST_ID_KEYS) || findNestedValue(requestParams, REQUEST_ID_KEYS) || ''
  const token = findNestedValue(response, TOKEN_KEYS) || findNestedValue(requestParams, TOKEN_KEYS) || ''

  return {
    req_id: reqId ? String(reqId) : '',
    token: token ? String(token) : '',
    caller_type: 1,
    api_type: ConstData.EKYC_DYNA_API_LOG_API_TYPE.LIVE_WEB,
    req_content: stringifyPayload({
      action,
      request: sanitizedRequestParams
    }),
    resp_content: stringifyPayload(sanitizedResponse)
  }
}

export function buildDynaLiveWebCallbackReportDedupeKey(scene = '', callback = {}) {
  return ['dynaLiveWebCallback', scene, callback.requestId || '', callback.bizNum || '', callback.msg || ''].join('|')
}

export function reportDynaLiveWebApiLog(action, requestParams = {}, response = null, options = {}) {
  const dedupeKey = options && options.dedupeKey ? String(options.dedupeKey) : ''

  if (dedupeKey) {
    if (REPORTED_DEDUPE_KEYS.has(dedupeKey)) {
      return Promise.resolve(null)
    }

    REPORTED_DEDUPE_KEYS.add(dedupeKey)
  }

  const payload = buildDynaLiveWebApiLogPayload(action, requestParams, response)

  return saveOcrApiLog(payload)
    .then(res => res)
    .catch(error => {
      debugLogger.exception('[utils/dyna-liveweb-api-log] saveApiLog fail', error, {
        action,
        reqId: payload.req_id
      })

      return null
    })
}
